import { apolloClient } from './graphql_client';
import { gql } from '@apollo/client';
import { markSessionActivity } from './session_activity';
import { getRememberMePreference } from './remember_me';
import { setAuthUserString } from './auth_storage';

interface LoginRole {
  name: string;
}

interface LoginUser {
  id: string;
  name: string;
  email: string;
  roles: {
    nodes: LoginRole[];
  };
}

export interface AuthUserData {
  accessToken: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  roles: string[];
}

export type PortalLoginResult =
  | { status: 'authenticated'; authData: AuthUserData }
  | {
      status: 'otp_required';
      challengeToken: string;
      emailHint: string;
      expiresIn?: number | null;
      username: string;
      password: string;
    }
  | { status: 'totp_required'; challengeToken: string; username: string; password: string }
  | { status: 'totp_setup_required'; challengeToken: string; username: string; password: string };

interface PortalLoginResponse {
  portalLogin: {
    requiresEmailOtp?: boolean | null;
    requiresTotp?: boolean | null;
    requiresTotpSetup?: boolean | null;
    challengeToken?: string | null;
    emailHint?: string | null;
    expiresIn?: number | null;
    authToken?: string | null;
    refreshToken?: string | null;
    deviceTrustToken?: string | null;
    deviceTrustExpiresIn?: number | null;
    user?: LoginUser | null;
  } | null;
}

const PORTAL_LOGIN_MUTATION = gql`
  mutation PortalLogin($username: String!, $password: String!, $deviceTrustToken: String) {
    portalLogin(
      input: {
        username: $username
        password: $password
        deviceTrustToken: $deviceTrustToken
      }
    ) {
      requiresEmailOtp
      requiresTotp
      requiresTotpSetup
      challengeToken
      emailHint
      expiresIn
      authToken
      refreshToken
      deviceTrustToken
      deviceTrustExpiresIn
      user {
        id
        name
        email
        roles {
          nodes {
            name
          }
        }
      }
    }
  }
`;

const PENDING_OTP_KEY = 'ngk_pending_email_otp';

export const storePendingEmailOtp = (payload: {
  challengeToken: string;
  emailHint: string;
  username: string;
  password: string;
}): void => {
  try {
    sessionStorage.setItem(PENDING_OTP_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

export const getPendingEmailOtp = (): {
  challengeToken: string;
  emailHint: string;
  username: string;
  password: string;
} | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_OTP_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearPendingEmailOtp = (): void => {
  try {
    sessionStorage.removeItem(PENDING_OTP_KEY);
  } catch {
    // ignore
  }
};

export const updatePendingEmailOtpChallenge = (
  challengeToken: string,
  emailHint?: string | null
): void => {
  const pending = getPendingEmailOtp();
  if (!pending) return;
  storePendingEmailOtp({
    ...pending,
    challengeToken,
    emailHint: emailHint || pending.emailHint,
  });
};

const assertUserAllowed = (roles: string[]) => {
  if (roles.includes('pending_user')) {
    throw new Error('Your account is pending approval. Please contact an administrator.');
  }
  if (roles.includes('rejected_user')) {
    throw new Error('Your account registration was rejected. Please contact an administrator.');
  }
  if (roles.includes('suspended_user')) {
    throw new Error('Your account has been suspended. Please contact an administrator.');
  }
};

export const persistAuthSession = async (
  authToken: string,
  user: LoginUser,
  refreshToken?: string | null,
  credentials?: { username: string; password: string } | null
): Promise<AuthUserData> => {
  const userRoles = (user.roles?.nodes || []).map((role: LoginRole) => role.name);
  assertUserAllowed(userRoles);

  const authData: AuthUserData = {
    accessToken: authToken,
    user: {
      id: user.id,
      name: user.name,
      email: user.email,
    },
    roles: userRoles,
  };

  setAuthUserString(JSON.stringify(authData), getRememberMePreference());
  markSessionActivity();

  try {
    const { storeCredentialsForRelogin, storeRefreshToken } = await import('./auth_relogin');
    if (credentials?.username && credentials?.password) {
      storeCredentialsForRelogin(credentials.username, credentials.password);
    }
    storeRefreshToken(refreshToken);
  } catch (error) {
    console.warn('Could not store credentials for relogin:', error);
  }

  return authData;
};

const INVALID_CREDENTIALS_MESSAGE = 'Invalid username or password.';

function collectErrorMessages(error: any): string[] {
  const messages: string[] = [];
  const lists = [
    error?.graphQLErrors,
    error?.errors,
    error?.cause?.errors,
    error?.cause?.graphQLErrors,
    error?.networkError?.result?.errors,
    error?.networkError?.errors,
    error?.protocolErrors,
  ];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (item?.message) messages.push(String(item.message));
    }
  }
  if (error?.message) messages.push(String(error.message));
  if (error?.networkError?.message) messages.push(String(error.networkError.message));
  if (error?.cause?.message) messages.push(String(error.cause.message));
  return messages;
}

function isCredentialFailure(text: string): boolean {
  return (
    text.includes('invalid username') ||
    text.includes('invalid password') ||
    text.includes('incorrect password') ||
    text.includes('wrong password') ||
    text.includes('invalid credentials') ||
    text.includes('authentication failed') ||
    text.includes('login=failed') ||
    text.includes('my-account') ||
    text.includes('unexpected token') ||
    text.includes('is not valid json') ||
    text.includes('<!doctype') ||
    text.includes('redirect') ||
    (text.includes('invalid') &&
      (text.includes('username') || text.includes('password') || text.includes('user')))
  );
}

function isTransportFailure(text: string, statusCode?: number): boolean {
  if (statusCode === 401 || statusCode === 403 || statusCode === 302 || statusCode === 301) {
    return false;
  }
  return (
    text.includes('failed to fetch') ||
    text.includes('networkerror') ||
    text.includes('network request failed') ||
    text.includes('load failed') ||
    text.includes('cors')
  );
}

const extractGraphqlError = (error: any, assumeCredentialFailure = false): string => {
  const messages = collectErrorMessages(error);
  const joined = messages.join(' ').toLowerCase();
  const statusCode =
    error?.networkError?.statusCode ||
    error?.statusCode ||
    error?.cause?.statusCode;

  if (
    assumeCredentialFailure ||
    isCredentialFailure(joined) ||
    statusCode === 401 ||
    statusCode === 403 ||
    statusCode === 301 ||
    statusCode === 302
  ) {
    return INVALID_CREDENTIALS_MESSAGE;
  }

  const graphQlMessage = messages.find((message) => {
    const lower = message.toLowerCase();
    return (
      message &&
      !lower.includes('failed to fetch') &&
      !lower.includes('status code') &&
      !lower.includes('response not successful') &&
      !lower.includes('combinedgraphqlerrors')
    );
  });
  if (graphQlMessage && graphQlMessage.length < 280) {
    return graphQlMessage;
  }

  if (isTransportFailure(joined, statusCode)) {
    return 'Unable to connect to the server. Please check your connection and try again.';
  }

  return INVALID_CREDENTIALS_MESSAGE;
};

/**
 * Step 1 of portal login: password check.
 * Returns JWT when OTP is disabled on WordPress, otherwise an email OTP challenge.
 */
export const loginWithWordPress = async (
  username: string,
  password: string
): Promise<PortalLoginResult> => {
  try {
    const { getEmailOtpTrust, storeEmailOtpTrust } = await import('./email_otp_trust');
    const deviceTrustToken = getEmailOtpTrust();

    const { data } = await apolloClient.mutate<PortalLoginResponse>({
      mutation: PORTAL_LOGIN_MUTATION,
      variables: { username, password, deviceTrustToken },
    });

    const payload = data?.portalLogin;
    if (!payload) {
      throw new Error('Invalid response from GraphQL server');
    }

    if (payload.deviceTrustToken) {
      storeEmailOtpTrust(payload.deviceTrustToken, payload.deviceTrustExpiresIn);
    }

    if (payload.requiresEmailOtp) {
      if (!payload.challengeToken) {
        throw new Error('Verification challenge was not created. Please try again.');
      }
      return {
        status: 'otp_required',
        challengeToken: payload.challengeToken,
        emailHint: payload.emailHint || 'your email',
        expiresIn: payload.expiresIn,
        username,
        password,
      };
    }

    if (payload.requiresTotp) {
      if (!payload.challengeToken) {
        throw new Error('Authenticator challenge was not created. Please try again.');
      }
      return {
        status: 'totp_required',
        challengeToken: payload.challengeToken,
        username,
        password,
      };
    }

    if (payload.requiresTotpSetup) {
      if (!payload.challengeToken) {
        throw new Error('Authenticator setup challenge was not created. Please try again.');
      }
      return {
        status: 'totp_setup_required',
        challengeToken: payload.challengeToken,
        username,
        password,
      };
    }

    if (!payload.authToken || !payload.user) {
      throw new Error('Invalid response from GraphQL server');
    }

    const authData = await persistAuthSession(
      payload.authToken,
      payload.user,
      payload.refreshToken,
      { username, password }
    );

    return { status: 'authenticated', authData };
  } catch (error: any) {
    console.error('WordPress GraphQL Login Error:', error);
    throw new Error(extractGraphqlError(error, true));
  }
};
